import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, Highlight, Icon, RowsSkeletonLoader } from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Associates, CustomerApplicationDetails } from 'types/customers'
import {
  ASSOCIATE_TYPE_TRANSLATION_KEYS,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID,
  REQUIRED_ACTION_CODE_ASSOCIATE_INFO_REQUIRED,
} from '../helpers/constants'

interface CustomerAssociatesTabProps {
  associates: CustomerApplicationDetails
  isV1CustomerOnboarding?: boolean
  associateData: Associates[] | undefined
  isLoadingAssociates: boolean
  isAssociateError: boolean
  refetchAssociates: () => void
}

const IDENTITY_DOC_TYPES = new Set(['PASSPORT', 'ID_CARD', 'DRIVER_LICENSE', 'RESIDENCE_PERMIT'])
const PROOF_OF_ADDRESS_TYPES = new Set(['PROOF_OF_ADDRESS', 'UTILITY_BILL', 'PROOF_OF_RESIDENCE'])

export const CustomerAssociatesTab = ({
  associates,
  isV1CustomerOnboarding,
  associateData,
  isLoadingAssociates,
  isAssociateError,
  refetchAssociates,
}: CustomerAssociatesTabProps) => {
  const { t } = useTranslation()

  if (isLoadingAssociates) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isAssociateError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetchAssociates} />
      </Box>
    )
  }

  const missingRoles =
    associates?.requiredActions
      ?.filter(
        ({ category, code }) =>
          category === REQUIRED_ACTION_CATEGORY_ASSOCIATES &&
          code !== REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID
      )
      .map(({ code, description }) =>
        isV1CustomerOnboarding && code === REQUIRED_ACTION_CODE_ASSOCIATE_INFO_REQUIRED
          ? {
              key: description,
              label: t(ASSOCIATE_TYPE_TRANSLATION_KEYS[description] ?? description),
            }
          : { key: code, label: t(ASSOCIATE_TYPE_TRANSLATION_KEYS[code] ?? code) }
      ) ?? []

  const associateIds = new Set((associates?.company?.associates ?? []).map(a => a.id))
  const existingAssociates = (associateData ?? []).filter(a => associateIds.has(a.id))
  const showMissingRoles = !(isV1CustomerOnboarding && existingAssociates.length > 0)

  return (
    <Box flexCol paddingY={16}>
      <SidePanelSection heading={t('associates.description')} />
      {showMissingRoles &&
        missingRoles.map(({ key, label }) => (
          <DataListItem
            key={key}
            id={key}
            layout="horizontal"
            title={label}
            body=""
            testId="customer-associate-missing-role"
            HeaderHighlight={
              <Highlight type="warning">{t('embeddedCustomers.status.infoRequired')}</Highlight>
            }
          />
        ))}

      {existingAssociates.map(associate => {
        const uploadedDocTypes = new Set((associate.documents ?? []).map(doc => doc.type))

        const identitySubmitted = [...uploadedDocTypes].some(docType =>
          IDENTITY_DOC_TYPES.has(docType)
        )
        const selfieSubmitted = uploadedDocTypes.has('SELFIE')
        const proofSubmitted = [...uploadedDocTypes].some(docType =>
          PROOF_OF_ADDRESS_TYPES.has(docType)
        )

        const iconGroups = [
          { key: 'identity', icon: 'Files' as const, submitted: identitySubmitted },
          { key: 'selfie', icon: 'UserCircle' as const, submitted: selfieSubmitted },
          { key: 'proof', icon: 'AddressBook' as const, submitted: proofSubmitted },
        ]

        return (
          <Box key={associate.id} flexCol className="gap-1">
            <DataListItem
              id={associate.id}
              layout="vertical"
              title={associate.types
                .map(type => t(ASSOCIATE_TYPE_TRANSLATION_KEYS[type] ?? type))
                .join(' / ')}
              body={associate.displayName}
              testId="customer-associate"
              HeaderHighlight={
                <Highlight
                  type={associate.validation?.status === 'COMPLETE' ? 'success' : 'warning'}
                >
                  {associate.validation?.status === 'COMPLETE'
                    ? t('embeddedCustomers.status.verified')
                    : t('embeddedCustomers.status.infoRequired')}
                </Highlight>
              }
            />
            <Box testId="associate-doc-icons" className="flex flex-row gap-3 pb-2 justify-end">
              {iconGroups.map(({ key, icon, submitted }) => (
                <Box
                  key={key}
                  className={
                    submitted
                      ? 'text-foreground-action-secondary'
                      : 'text-foreground-action-disabled'
                  }
                >
                  <Icon name={icon} size="medium" />
                </Box>
              ))}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
